import { HelpButton } from 'components/molecules/HelpButton';

import { Page } from 'components/organisms/Page';
import { Header } from 'components/templates/Header';
import useTheme from '@mui/material/styles/useTheme';
import * as S from './InfoPage.style';

type InfoPageProps = {
  title: string;
  subtitle: string | React.ReactNode;
  illustrationSvgElement: React.ReactNode;
  illustrationMobileMaxWidth?: string;
  children?: React.ReactNode;
};

/**
 * A template component that displays an informational page with an illustration,
 * title, subtitle, and optional additional content. The layout is responsive,
 * adjusting typography sizes based on screen size.
 *
 * @param props - The component props
 * @param props.title - The main title text to display as an h1 element
 * @param props.subtitle - The subtitle text to display below the title as a paragraph
 * @param props.illustrationSvgElement - SVG illustration element to display at the top
 * @param props.illustrationMobileMaxWidth - Optional override for the illustration's max width below the `md` breakpoint (defaults to `16rem`)
 * @param props.children - Optional additional content to render below the title and subtitle
 * @returns A React component that renders an informational page layout
 */
export function InfoPage({
  illustrationSvgElement,
  illustrationMobileMaxWidth,
  title,
  subtitle,
  children,
}: InfoPageProps) {
  const theme = useTheme();

  return (
    <Page
      header={
        <Header
          to="/"
          style={{
            $backgroundColor: theme.palette.background.default,
            $secondaryBackgroundColor: theme.palette.background.default,
          }}
        >
          <HelpButton />
        </Header>
      }
    >
      <S.Container>
        <S.Illustration
          $maxWidth={illustrationMobileMaxWidth}
          aria-hidden="true"
        >
          {illustrationSvgElement}
        </S.Illustration>

        <S.Title component="h1" isDisplay>
          {title}
        </S.Title>

        <S.Subtitle component="h2">{subtitle}</S.Subtitle>

        {children}
      </S.Container>
    </Page>
  );
}
